import { useState } from 'react'
import { PageContainer, ProTable, type ProColumns } from '@ant-design/pro-components'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Alert, App, Button, Image, Modal } from 'antd'
import dayjs from 'dayjs'
import { Plus } from 'lucide-react'
import type { LicenseListInput, LicenseRow } from '@super-mega/server/contracts/license'
import type { ServiceEnterpriseDraft } from '@super-mega/server/contracts/order'
import { CREATED_AT_SORT_DIRECTIONS } from '@/const/list.ts'
import { RANGE_PRESETS } from '@/const/range-presets.ts'
import {
  isSensitiveLicenseKeyword,
  LicenseCreateModal,
  LicenseDocumentPreview,
  licenseListSearchSchema
} from '@/features/license/index.ts'
import { UserAvatarName } from '@/features/profile/index.ts'
import { EnterpriseCreateModal } from '@/features/service-enterprise/index.ts'
import { useMemberCreatorOptions, usePageTableNavigation } from '@/hooks/index.ts'
import { usePersistedListQuery } from '@/hooks/use-persisted-list-query.ts'
import { useTRPC } from '@/trpc/react.ts'
import { createListQueryPersistence } from '@/utils/list-query-persistence.ts'
import {
  getCreatedAtSearch,
  getCreatedAtSortOrder,
  getDateRangeFormValue,
  getPositiveIntegerIdSearchValue,
  getTableSortOrder,
  type DateRangeFormValue
} from '@/utils/list-query.ts'
import { proRemoteSearch } from '@/utils/pro-select-search.ts'

const listQuery = createListQueryPersistence('super-mega:licenses:list-query', licenseListSearchSchema)

export const Route = createFileRoute('/_authenticated/licenses/')({
  validateSearch: listQuery.validateSearch,
  beforeLoad: (options) => {
    listQuery.restore(options)
  },
  head: () => ({ meta: [{ title: '执照管理 · Super Mega' }] }),
  component: LicensePage
})

type SearchValues = {
  idSearch?: string
  keyword?: string
  creatorMemberId?: number
  createdAtRange?: DateRangeFormValue
}

function LicenseEvidence({ row, onClose }: { row: LicenseRow; onClose: () => void }) {
  const [open, setOpen] = useState(true)
  const trpc = useTRPC()
  const ids = [row.businessLicenseFileId, row.frontFileId].filter((id): id is number => id !== null)
  const files = useQuery(trpc.file.getUrls.queryOptions({ ids }))
  const documents = [
    { id: row.businessLicenseFileId, title: '营业执照', description: row.unifiedSocialCreditCode },
    { id: row.frontFileId, title: '法人身份证人像面', description: `${row.identityName} · ${row.maskedIdNumber}` }
  ]
  return (
    <Modal
      open={open}
      title="证件资料"
      footer={<Button onClick={() => setOpen(false)}>关闭</Button>}
      onCancel={() => setOpen(false)}
      afterClose={onClose}
      width="64rem"
    >
      <div className="mb-5">
        <h3 className="mb-1 text-base font-semibold">{row.name}</h3>
        <p className="m-0 text-sm text-slate-500">点击图片查看原图，支持放大、旋转和切换证件。</p>
      </div>
      {files.isError ? (
        <Alert
          className="mb-4!"
          type="error"
          title={files.error.message}
          action={
            <Button size="small" onClick={() => void files.refetch()}>
              重新加载
            </Button>
          }
        />
      ) : null}
      <Image.PreviewGroup>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {documents.map((document) => (
            <section
              key={document.title}
              className="min-w-0 rounded-xl border border-slate-200 p-4 dark:border-slate-700"
            >
              <h4 className="mb-3 text-sm font-semibold">{document.title}</h4>
              <LicenseDocumentPreview
                src={files.data?.find((file) => file.id === document.id)?.url}
                title={document.title}
                large
                loading={files.isPending}
              />
              <p className="mt-3 mb-0 text-xs break-all text-slate-500">{document.description}</p>
            </section>
          ))}
        </div>
      </Image.PreviewGroup>
    </Modal>
  )
}

function LicensePage() {
  const { message, modal } = App.useApp()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const { currentSession } = Route.useRouteContext()
  const navigate = Route.useNavigate()
  const { search, navigateSearch } = usePersistedListQuery({
    persistence: listQuery,
    raw: Route.useSearch(),
    accountId: currentSession.id,
    navigate
  })
  const [sensitiveKeyword, setSensitiveKeyword] = useState<string>()
  const query: LicenseListInput = { ...search, keyword: search.keyword ?? sensitiveKeyword }
  const table = usePageTableNavigation<LicenseListInput, SearchValues>({
    search: query,
    navigate: navigateSearch,
    defaultSearch: { current: 1, pageSize: 20 },
    showTotal: (total) => `共 ${total} 条`,
    getFormInitialValues: (value) => ({
      idSearch: value.id?.toString(),
      keyword: value.keyword,
      creatorMemberId: value.creatorMemberId,
      createdAtRange: getDateRangeFormValue(value.createdFrom, value.createdTo)
    }),
    getSearchFromForm: (values) => {
      const keyword = values.keyword?.trim() || undefined
      const sensitive = isSensitiveLicenseKeyword(keyword)
      setSensitiveKeyword(sensitive ? keyword : undefined)
      return {
        id: getPositiveIntegerIdSearchValue(values.idSearch),
        keyword: sensitive ? undefined : keyword,
        creatorMemberId: values.creatorMemberId,
        ...getCreatedAtSearch(values.createdAtRange)
      }
    }
  })
  const [creating, setCreating] = useState(false)
  const [viewing, setViewing] = useState<LicenseRow>()
  const [enterpriseDraft, setEnterpriseDraft] = useState<{ companyId: number; draft: ServiceEnterpriseDraft }>()
  const [enterpriseOpen, setEnterpriseOpen] = useState(false)
  const [loadingEnterpriseId, setLoadingEnterpriseId] = useState<number>()
  const canCreateEnterprise = currentSession.isSuperAdmin || currentSession.permissions.includes('subject.create')
  const registerEnterprise = async (row: LicenseRow) => {
    if (enterpriseDraft?.companyId === row.id) {
      setEnterpriseOpen(true)
      return
    }
    setLoadingEnterpriseId(row.id)
    try {
      const [company, identity] = await Promise.all([
        queryClient.fetchQuery({ ...trpc.company.reusable.queryOptions({ id: row.id }), staleTime: 0 }),
        queryClient.fetchQuery({ ...trpc.identity.reusable.queryOptions({ id: row.identityId }), staleTime: 0 })
      ])
      if (!company || !identity) {
        void message.error('执照或法人档案已不可用，请刷新列表后重试')
      } else if (company.legalRepresentativeIdentityId !== identity.id) {
        void message.error('法人关系已发生变化，请刷新列表后重新核对')
      } else {
        setEnterpriseDraft({
          companyId: row.id,
          draft: {
            customerId: 0,
            kind: 'registeredCompany',
            displayName: company.name,
            documents: {
              company: { mode: 'existing', id: company.id, expectedUpdatedAt: dayjs(company.updatedAt).toISOString() },
              identity: {
                mode: 'existing',
                id: identity.id,
                expectedUpdatedAt: dayjs(identity.updatedAt).toISOString()
              },
              confirmLegalRepresentative: false,
              clearPrimaryIdentity: false
            }
          }
        })
        setEnterpriseOpen(true)
      }
    } catch (cause) {
      void message.error(cause instanceof Error ? cause.message : '读取档案失败')
    }
    setLoadingEnterpriseId(undefined)
  }
  const canManage =
    currentSession.isSuperAdmin ||
    (['license.recognize'] as const).every((permission) => currentSession.permissions.includes(permission))
  const list = useQuery({ ...trpc.license.list.queryOptions(query), placeholderData: keepPreviousData })
  const memberCreator = useMemberCreatorOptions('company.read', query.creatorMemberId)
  const columns: ProColumns<LicenseRow>[] = [
    {
      title: 'ID',
      dataIndex: 'idSearch',
      hideInTable: true,
      fieldProps: { placeholder: '输入执照资料 ID', inputMode: 'numeric' }
    },
    {
      title: '关键词',
      dataIndex: 'keyword',
      hideInTable: true,
      fieldProps: { maxLength: 100, placeholder: '法人姓名、身份证号、企业代码或企业名称' },
      tooltip: '身份证号等长数字搜索仅在当前页面有效，不保存到链接中'
    },
    {
      title: '创建人',
      dataIndex: 'creatorMemberId',
      hideInTable: true,
      valueType: 'select',
      fieldProps: {
        allowClear: true,
        options: memberCreator.options,
        loading: memberCreator.isLoading,
        ...proRemoteSearch(memberCreator.onSearch)
      }
    },
    {
      title: '创建时间',
      dataIndex: 'createdAtRange',
      hideInTable: true,
      fieldProps: { presets: RANGE_PRESETS },
      valueType: 'dateRange'
    },
    {
      title: 'ID',
      dataIndex: 'id',
      search: false,
      copyable: true,
      renderText: (value: number) => String(value),
      width: '6rem'
    },
    { title: '企业名称', dataIndex: 'name', search: false, ellipsis: true, width: '18rem' },
    { title: '统一社会信用代码', dataIndex: 'unifiedSocialCreditCode', search: false, copyable: true, width: '15rem' },
    { title: '法人姓名', dataIndex: 'identityName', search: false, width: '8rem' },
    { title: '法人资料 ID', dataIndex: 'identityId', search: false, copyable: true, width: '8rem' },
    { title: '身份证号', dataIndex: 'maskedIdNumber', search: false, width: '13rem' },
    {
      title: '创建人',
      dataIndex: 'creator',
      search: false,
      width: '10rem',
      render: (_, row) => <UserAvatarName name={row.creator.name} avatar={row.creator.avatar} size={28} />
    },
    {
      title: '创建时间',
      dataIndex: 'createdAt',
      search: false,
      valueType: 'dateTime',
      sorter: true,
      sortDirections: CREATED_AT_SORT_DIRECTIONS,
      width: '12rem',
      sortOrder: getTableSortOrder(query.sortOrder)
    },
    {
      align: 'center',
      title: '操作',
      valueType: 'option',
      fixed: 'right',
      width: '16rem',
      render: (_, row) => (
        <div className="flex justify-center gap-1">
          <Button type="link" onClick={() => setViewing(row)}>
            查看证件
          </Button>
          {canCreateEnterprise && (
            <Button
              type="link"
              loading={loadingEnterpriseId === row.id}
              disabled={Boolean(loadingEnterpriseId)}
              onClick={() => {
                if (enterpriseDraft && enterpriseDraft.companyId !== row.id) {
                  modal.confirm({
                    title: '改用这份执照登记服务对象？',
                    content: '当前尚未保存的企业登记草稿将被替换；所属客户仍需手动选择。',
                    onOk: () => registerEnterprise(row)
                  })
                } else {
                  void registerEnterprise(row)
                }
              }}
            >
              登记为服务对象
            </Button>
          )}
        </div>
      )
    }
  ]
  return (
    <PageContainer title="执照管理" breadcrumbRender={false} content="上传营业执照和法人身份证，核对后建立一对一关联。">
      {list.isError ? (
        <Alert
          className="mb-4!"
          type="error"
          showIcon
          title={list.error.message}
          action={<Button onClick={() => void list.refetch()}>重试</Button>}
        />
      ) : null}
      <ProTable<LicenseRow, SearchValues>
        formRef={table.formRef}
        form={{ initialValues: table.formInitialValues }}
        rowKey="id"
        columns={columns}
        dataSource={list.data?.data ?? []}
        loading={list.isFetching}
        columnsState={{ persistenceKey: 'licenses:table', persistenceType: 'localStorage' }}
        search={{ defaultCollapsed: false, labelWidth: 'auto' }}
        scroll={{ x: '110rem' }}
        onSubmit={table.onSubmit}
        onReset={() => {
          setSensitiveKeyword(undefined)
          table.onReset()
        }}
        onChange={
          /* eslint-disable max-params -- Ant Design Table 的回调签名由组件定义。 */
          (_pagination, _filters, sorter, extra) => {
            if (extra.action !== 'sort') {
              return
            }
            const sort = Array.isArray(sorter) ? sorter[0] : sorter
            void navigate({
              search: (previous) => ({
                ...previous,
                current: 1,
                sortField: 'createdAt',
                sortOrder: getCreatedAtSortOrder(sort.order)
              })
            })
          }
          /* eslint-enable max-params */
        }
        pagination={{
          ...table.pagination,
          total: list.data?.total ?? 0,
          onChange: (current, pageSize) =>
            table.pagination.onChange(pageSize === search.pageSize ? current : 1, pageSize)
        }}
        options={{ reload: () => void list.refetch() }}
        toolBarRender={() =>
          canManage
            ? [
                <Button
                  key="create"
                  type="primary"
                  icon={<Plus aria-hidden size={16} />}
                  onClick={() => setCreating(true)}
                >
                  新增执照关联
                </Button>
              ]
            : []
        }
      />
      {creating ? (
        <LicenseCreateModal
          onClose={() => setCreating(false)}
          onSaved={async () => {
            setCreating(false)
            await Promise.all([
              queryClient.invalidateQueries(trpc.license.list.queryFilter()),
              queryClient.invalidateQueries(trpc.company.search.queryFilter()),
              queryClient.invalidateQueries(trpc.identity.search.queryFilter()),
              queryClient.invalidateQueries(trpc.company.reusable.queryFilter())
            ])
          }}
        />
      ) : null}
      {viewing ? <LicenseEvidence row={viewing} onClose={() => setViewing(undefined)} /> : null}
      {enterpriseDraft && (
        <EnterpriseCreateModal
          key={enterpriseDraft.companyId}
          open={enterpriseOpen}
          onOpenChange={setEnterpriseOpen}
          initialDraft={enterpriseDraft.draft}
          onSuccess={() => {
            setEnterpriseOpen(false)
            setEnterpriseDraft(undefined)
          }}
        />
      )}
    </PageContainer>
  )
}
